/**
 * VRG-265 — what a scan session is FOR, sent to `POST /admins/admin/toggle-scan-session`.
 *
 * Mirrors the API's `ScanSessionPurpose` (packages/types) rather than importing it: `apps/platform`
 * does not depend on `@repo/types` (the two files that do import it are pre-existing typecheck
 * errors), so a shared import here would add three more to that count for a two-member union.
 *
 * `certificate` (the API's default when omitted) may mint or mutate. `filter` is READ-ONLY —
 * the API refuses create-certificates / add-package / replace-certificate-tag on it — and needs
 * no productId, which is what lets scan-to-filter open a session at all.
 *
 * VERA-455 — `receive` lets a PHONE take a box into inventory. Tapping a `/pkg/` handle in one
 * of these receives that package (`at_vera` -> `available`) instead of staging its tags, which
 * is the only thing a box tap could do before. It mints nothing: the API refuses every
 * certificate path on it, exactly as it does a filter session. Opening one requires the
 * `tags_inventory_receive` permission — checked when the session is created, because the phone
 * that will use it is unauthenticated and cannot present a permission of its own.
 *
 * KEEP IN LOCKSTEP with the API's union: adding a member here without adding it there gives a
 * purpose the server silently defaults to `certificate`, which is the one direction that
 * matters (it would mint rather than refuse).
 */
export type ScanSessionPurpose = 'certificate' | 'filter' | 'receive'

export enum Actions {
  START_SCAN_SESSION = "START_SCAN_SESSION",
  SET_SCAN_SESSION_ID = "SET_SCAN_SESSION_ID",
  END_SCAN_SESSION = "END_SCAN_SESSION"
}

export interface IValidationScanSessionMessageCallbackParams {
  tagId?: string,
  deviceRegistered?: boolean,
  // The SSE scan-result fields, straight off `pushScanResult`'s payload — the saga hands the
  // parsed event through verbatim, so everything the API emits is readable here. These were
  // absent from the type, so every consumer reached for them through an `as any` cast (and one
  // of them read a variable that did not exist at all, silently swallowing the server's own
  // refusal text).
  // 'removed' is not a scan outcome — it is a tag being taken back OUT of the session (a
  // mis-scan corrected from the pending list). It rides the same channel so both surfaces
  // agree on the count without a second transport.
  status?: 'created' | 'existing' | 'pending' | 'error' | 'removed',
  certificateId?: string,
  error?: string,
  /**
   * A non-blocking advisory about a scan that nonetheless LANDED — today, the scanned tag not
   * being the tag this product's default names. Deliberately distinct from `error`, which
   * means the scan was refused: the two demand opposite next actions from the operator, so
   * they must never render alike.
   */
  warning?: string,
  /**
   * WHAT THE SESSION IS FOR — and so what `status` above MEANS, which `status` alone does not
   * say. `existing` is a dead end in a `certificate` session (nothing will be minted for that
   * tag) and the success case in a `filter` one (finding that certificate is the entire point).
   *
   * Emitted with every scan result rather than looked up per surface, because the other
   * consumer of this payload — the phone — is unauthenticated and holds nothing but a session
   * id, and read `existing` as a refusal in both purposes until this arrived.
   *
   * Absent reads as `certificate`: every session predating VRG-265 was one.
   */
  purpose?: ScanSessionPurpose,
  // True when this result came from expanding a PACKAGE handle rather than a tap on a tag —
  // derived server-side from the scanned URL's shape (`/pkg/<uuid>`), never from the chip
  // family. Lets the UI say "package" where it would otherwise say "tag".
  viaPackage?: boolean,
  /**
   * WHICH package a `viaPackage` result came from. `viaPackage` alone only says a row did not
   * come from a tap, which is not enough to review a session that mixes hand-scans with two
   * different boxes — the operator's question is "which box was that from", and an unattributed
   * "from a package" marker cannot answer it. Name is the label; id is the fallback (and what
   * tells two same-named boxes apart).
   */
  packageId?: string,
  packageName?: string,
  /**
   * The SESSION itself has ended — not a tag event at all.
   *
   * Emitted when the phone's "End session" control tears the session down, so the
   * Create-Certificates modal learns its session is dead and closes, instead of sitting open
   * over a session nothing can scan into. Rides this channel rather than a new one; the saga's
   * fall-through branch passes it verbatim because it carries no tag status.
   */
  sessionEnded?: boolean,
  /**
   * WHAT THE TAG CAN DO — "Verify", "Verify + Tamper", "Connect", … — so a review row can lead
   * with something a person recognises rather than with the chip's hex UID.
   *
   * ⚠️ CAPABILITY, NEVER THE CHIP FAMILY. The family (NTAG 424 DNA, NTAG 213, …) is internal
   * fulfilment detail and must not reach any operator- or customer-facing surface. The API
   * derives this from the tag-shop SKU via `parseTagSku` precisely so a client cannot leak a
   * value it was never sent — so read this field, and never try to infer a tag's identity from
   * anything else on the payload.
   *
   * Absent or `null` = not derivable (a tag with no catalog SKU — legacy import, seed data —
   * or an unrecognised capability code). Fall back to a neutral label; never guess.
   */
  capability?: string | null,
  /**
   * VERA-455 — the ONE refusal on this channel that has an in-app way out, carrying what is
   * needed to offer it.
   *
   * A tag still `possession_state: 'at_vera'` is refused because our data says Vera holds it,
   * which is exactly the case where the operator is standing in front of the box holding it.
   * So the refusal ships the receipt context and the surface prompts to receive, per
   * `working-conventions.md` §46 (a block must leave its resolving action reachable).
   *
   * BRANCH ON PRESENCE, never on `error`'s text — the sentence is pinned by the docs' scan
   * test-plan and would take the way out with it if it were ever reworded.
   *
   * `packageId: null` means the tag belongs to no package, so there is nothing to receive:
   * explain it, do NOT offer a prompt that would transition zero rows. `tagCount` is how many
   * tags a receipt moves, which is the whole package, not just the one scanned.
   */
  notReceived?: { packageId: string | null; packageName: string | null; tagCount: number },
  /**
   * VERA-455 — a package was RECEIVED into inventory by a phone paired to this session.
   *
   * Not a tag event and not a scan result: it carries no `tagId` and no `status`, so it arrives
   * through the saga's fall-through branch verbatim, the same way `sessionEnded` and
   * `deviceRegistered` do.
   *
   * ITS OWN FIELD RATHER THAN THE `error` CHANNEL, which is where every other package-level
   * sentence rides (`announcePackageNotice`). That channel is rendered in the refusal treatment
   * — red text, red count pill — so reporting a successful receipt on it would paint the happy
   * path as a failure. A re-tap of an already-received box does NOT arrive here: it moved
   * nothing, so it is a notice, not a receipt.
   *
   * `received` is what actually moved; `total` is the whole box, which is the scope the receipt
   * acted on. They differ when part of the box was already on hand.
   */
  packageReceived?: { packageId: string; packageName?: string; received: number; total: number },
}
export type IValidationScanSessionMessageCallback = (params: IValidationScanSessionMessageCallbackParams) => void;


export interface IValidationStartScanSessionCallbackParams {
  success: boolean,
  scanSessionId: string,
  registerDeviceURL: string,
  closeEventSourceFn?: Function
}
export type IValidationStartScanSessionCallback = (params: IValidationStartScanSessionCallbackParams) => void;
export interface ActionScanSessionHandler<SSSC = IValidationStartScanSessionCallback, SSMC = IValidationScanSessionMessageCallback> {
  type: string
  startScanSessionCallback?: SSSC,
  scanSessionMessageCallback?: SSMC,
  productId?: string,
  /**
   * VRG-265 — what this session is FOR, sent to the API rather than inferred from whether a
   * `productId` was supplied. `filter` opens a read-only session (the API refuses
   * create-certificates / add-package / replace-tag on it); omitted means `certificate`, which
   * is what every pre-existing caller gets.
   */
  purpose?: ScanSessionPurpose,
}

export const SCAN_SESSION_ID_RESPONSE_FIELD = "scanSessionId";

export type GetScannSessionIdPayload = string;
export type ToggleScanSessionPayload = {
  turnOn: boolean
  productId?: string
  purpose?: ScanSessionPurpose
}